import { noop } from "lodash";

import {
    isFunction,
    isNonEmptyArray,
    isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";
import { notify } from "@cayuse/shared-react.components.banner-notification";

import store, { dispatch } from "store/store";
import { form as constants } from "store/constants";
import { actionLoaderFactory } from "store/factories/fetchable";

import {
    dateReadFormatter,
    formatConflictingFields,
    formatDatesInFormPayload,
    notifyError,
    transformFetchedFormModel,
} from "utils";
import Helper from "components/FormResponse/helper";
import {
    ERROR_STATUS_CODE_409,
    IS_DEV,
    ERROR_STATUS_CODE_403,
    ERROR_STATUS_CODE_406,
} from "globals/constants";
import { api } from "services";
import { CompareSnapshotData } from "utils/forms/compareSnapshotData";
import { resetForm } from "./form";

const formEntity = actionLoaderFactory(constants, constants.FORM_ENTITY);

const compareSnapshotData = new CompareSnapshotData();

const isModification = () => {
    const modificationNumber =
        store.getState().modifications.content.latestModificationNumber;
    return modificationNumber > 0;
};

const isSubaward = entity => {
	return entity?.submissionType === "subaward";
};

export const setFormEntity = (payload) => {
    let data = payload;
    if (isFunction(payload)) {
        data = payload(store.getState().form.entity);
    }
    formEntity.setData(data);
};

export const setChangedFormKeys = (value = null) => {
    const changedFormKeys = store.getState().form.entity.changedFormKeys;

    let payload;
    if (value) {
        payload = new Set([...changedFormKeys, value]);
    } else {
        payload = new Set();
    }

    dispatch({
        type: constants.FORM_ENTITY_SET_ENTITY_CHANGED_FORM_KEYS,
        payload,
    });
};

export const setFormContentKeys = (payload) => {
    if (!payload.payloadRootContentKey || !payload.payloadContentType) {
        throw new Error(
            "payloadRootContentKey and payloadContentType are required"
        );
    }

    dispatch({
        type: constants.FORM_ENTITY_SET_CONTENT_KEYS,
        payload,
    });
};

export const setFormEntityRequestsOptions = (payload) => {
    dispatch({
        type: constants.SET_ENTITY_FORM_REQUEST_OPTIONS,
        payload: {
            get: payload?.get ?? null,
            update: payload?.update ?? null,
        },
    });
};

const getFormEntityVariables = async (entity, modificationNumber) => {
    try {
        let variables = await api.form.getVariables({
            id: entity.caseInstanceId,
            modificationNumber,
        });

        variables = formatDatesInFormPayload(variables, dateReadFormatter);
        variables = Helper.removeInconsistentAttachmentObjects(variables);

        return variables;
    } catch (error) {
        const message =
            IS_DEV && isNonEmptyString(error.message)
                ? error.message
                : store.getState().dictionary.common.formResponse
                      .unableLoadData;
        notifyError(message, error);
        formEntity.setError(true);
    }
};

const getFormEntity =
    ({ isRefresh } = { isRefresh: false }) =>
    async (requestOptions, entity) => {
        return formEntity.createLoaderAction(
            async () => {
                const [variables, model] = await Promise.all([
                    getFormEntityVariables(entity, requestOptions.modificationNumber),
                    api.form.getCaseModel({ id: entity.caseInstanceId }),
                ]);

                return {
                    model,
                    variables,
                };
            },
            [
                ({ model, variables }) => {
                    const formConfig = transformFetchedFormModel(model);
                    const tabsConfig = Helper.updateFormData(formConfig);

                    const extraSettings =
                        formConfig.rows[0].cols[0].extraSettings
                            .layoutDefinition.rows[0].cols[0].extraSettings;
                    const { uploadTypes, justActivatedSectionLabel } =
                        requestOptions.get;

                    extraSettings.activetab = Helper.getActiveTab(
                        extraSettings,
                        uploadTypes,
                        justActivatedSectionLabel
                    );

                    const response = {
                        // set variables, changedVariables so that they are not empty when you work with them
                        variables,
                        changedVariables: variables,
                        formConfig,
                        tabsConfig,
                    };

                    setFormEntity(response);

                    return response;
                },
                (error, { getState }) => {
                    const message =
                        IS_DEV && isNonEmptyString(error.message)
                            ? error.message
                            : getState().dictionary.common.formResponse
                                  .unableLoadData;
                    notifyError(message, error);
                    formEntity.setError(true);
                },
            ],
            { isRefresh }
        );
    };

export const loadFormEntity = (formEntityRequestsOptions, entity) => {
    // store request options and entity to br able get this data for other requests
    setFormEntityRequestsOptions(formEntityRequestsOptions);
    setFormEntity({ data: entity, documentVersion: entity.documentVersion });

    return getFormEntity()(formEntityRequestsOptions, entity);
};

/**
 * Load variables from the server and compare them with the actual variables.
 * * Find conflicts and return result of comparing
 * * Replace form values directly using form ref
 * @returns {Promise<{mergedData: Record<string, any>, conflicts: ComparedObject[]}>}
 */
const compareFormData = async (payload = null, clearNextRequest = noop) => {
    const {
        payloadContentType,
        data: entity,
        changedVariables,
        changedFormKeys,
    } = store.getState().form.entity;

    // load server variables
    const serverVariables = await getFormEntityVariables(entity, entity.modificationNumber);

    let pathValueArray = [],
        conflicts = [],
        mergedData = {};

    try {
        // compare data and get conflicts and result of comparing
        const result = compareSnapshotData.compare(
            // use variables that was previously saved (source)
            changedVariables,
            // use actual variables that user typed in the form
            payload ?? changedVariables,
            // use server variables
            serverVariables,
            {
                formElements: entity.formElements,
                payloadKey: isModification() && !isSubaward(entity)
                    ? payloadContentType
                    : `root.${payloadContentType}`,
                changedFormKeys: changedFormKeys,
            }
        );

        pathValueArray = result.pathValueArray;
        conflicts = result.conflicts;
        mergedData = result.mergedData;
    } catch (error) {
        console.log("compareSnapshotData.compare error: ", error);
        console.trace();
        throw error;
    }

    // clear next request in requests chain
    if (pathValueArray.length > 0) {
        clearNextRequest();
    }

    // set merge data into the form
    pathValueArray.forEach(([path, value]) => {
        Helper.formRef.setPayload(path, value);
    });

    // dispatch event to call save form
    if (pathValueArray.length === 0) {
        Helper.formRef.onEvent(
            "Payload.afterChange",
            undefined,
            Helper.formRef.getPayload(),
            Helper.formRef
        );
    }

    return {
        conflicts,
        mergedData,
    };
};

export const editForm = async (edit, onReload) => {
    const {
        data: entity,
        documentVersion,
        requestsOptions: { update: options },
    } = store.getState().form.entity;

    try {
        const queryParams = {
            version: documentVersion ?? entity.documentVersion,
            modificationNumber : entity.modificationNumber
        };

        const response = await api.form[edit ? "editForm" : "cancelEditForm"](
            { url: options.recordRevisionUrl ?? options.url },
            queryParams,
            {},
            { numRetries: 0, notifyUponError: true }
        );

        setFormEntity({ documentVersion: Number(response.documentVersion) });
        setFormTimeoutInfo(response);
        setFormEntityData({editLock: response.editLock});

        return response;
    } catch (error) {
        if (error.response && error.response.status === ERROR_STATUS_CODE_409) {
            const { value: version, message } = await error.response.json();

            // do nothing and retry save form payload
            if (message.includes("Can not acquire lock")) {
                // retry update
                return editForm(edit, onReload);
            }

            // store last valid version
            setFormEntity({ documentVersion: Number(version) });
            return editForm(edit, onReload);
        }
        if (error.response && error.response.status === ERROR_STATUS_CODE_403) {
            setFormEditLocked(true);
            setFormEditMode(false);
            notify(
                store.getState().dictionary.common.formResponse
                    .anotherUserEditingWarning,
                {
                    type: "warning",
                    autoClose: true,
                }
            );
            if (isFunction(onReload)) {
                onReload();
            }
        } else {
            console.error(error);
        }
    }
};

export const checkForm = async (payload, onReload) => {
    const {
        data: entity,
        documentVersion,
        requestsOptions: { update: options },
    } = store.getState().form.entity;

    try {
        const queryParams = {
            version: documentVersion ?? entity.documentVersion,
        };

        const response = await api.form.checkForm(
            { url: options.url },
            queryParams,
            { ...payload, modificationNumber: Number(entity.modificationNumber) },
            { numRetries: 0, notifyUponError: true }
        );

        setFormEntity({
            changedVariables: payload,
            documentVersion: Number(response.documentVersion),
        });
        return response;
    } catch (error) {
        if (error.response.status === ERROR_STATUS_CODE_409) {
            const { value: version } = await error.response.json();

            // store last valid version
            setFormEntity({ documentVersion: Number(version) });
            return checkForm(payload, onReload);
        }
        if (error.response.status === ERROR_STATUS_CODE_403) {
            setFormEditLocked(true);
            setFormEditMode(false);
            if (isFunction(onReload)) {
                onReload();
            }
            resetForm();
        } else {
            throw error;
        }
    }
};

export const updateLock = async () => {
    const {
        data: entity,
        documentVersion,
        requestsOptions: { update: options },
    } = store.getState().form.entity;

    try {
        const queryParams = {
            updateLock: true,
            version: documentVersion ?? entity.documentVersion,
            modificationNumber : entity.modificationNumber
        };

        const response = await api.form.updateLock(
            { url: options.recordRevisionUrl ?? options.url },
            queryParams,
            {},
            { numRetries: 0, notifyUponError: true }
        );

        setFormEntity({ documentVersion: Number(response.documentVersion) });
        setFormTimeoutInfo(response);

        return response;
    } catch (error) {
        if (error.response.status === ERROR_STATUS_CODE_409) {
            const { value: version } = await error.response.json();

            // store last valid version
            setFormEntity({ documentVersion: Number(version) });
            return updateLock();
        }
        if (error.response.status === ERROR_STATUS_CODE_403) {
            setFormEditLocked(true);
            notify(
                store.getState().dictionary.common.formResponse
                    .anotherUserEditingWarning,
                {
                    type: "warning",
                    autoClose: true,
                }
            );
            resetForm();
        } else {
            throw error;
        }
    }
};

export const updateForm = async (payload, onReload) => {
    const {
        data: entity,
        documentVersion,
        requestsOptions: { update: options },
    } = store.getState().form.entity;

    let formattedPayload = Helper.removeInconsistentAttachmentObjects(payload);

    try {
        const queryParams = {
            version: documentVersion ?? entity.documentVersion,
        };

        setFormSavingIndicator(true);

        let response;

        response = await api.form.saveForm(
            { url: options.url },
            queryParams,
            { ...formattedPayload, modificationNumber : Number(entity.modificationNumber) },
            { numRetries: 0, notifyUponError: true }
        );

        // store version and set saved payload as source
        setFormEntity({
            documentVersion: Number(response.documentVersion),
            variables: formattedPayload,
        });

        setFormEntityData({editLock: null});

        return response;
    } catch (error) {
        if (error.response.status === ERROR_STATUS_CODE_409) {
            const { value: version } = await error.response.json();

            // store last valid version
            setFormEntity({ documentVersion: Number(version) });
            return updateForm(payload);
        } else if (error.response.status === ERROR_STATUS_CODE_403) {
            setFormEditLocked(true);
            setFormEditMode(false);
            if (isFunction(onReload)) {
                onReload();
            }
            resetForm();
        } else {
            throw error;
        }
    } finally {
        setFormSavingIndicator(false);
    }
};

export const updateFormEntity = async (payload, clearNextRequest) => {
    const {
        data: entity,
        documentVersion,
        requestsOptions: { update: options },
    } = store.getState().form.entity;

    const modificationNumber =
        store.getState().modifications.content.latestModificationNumber;

    let formattedPayload = Helper.removeInconsistentAttachmentObjects(payload);

    try {
        const queryParams = {
            version: documentVersion ?? entity.documentVersion,
        };
        if (isModification()) {
            queryParams.modificationNumber = modificationNumber;
        }

        let response;

        response = await api.form.updateFormEntityVersion(
            { url: options.url },
            queryParams,
            formattedPayload,
            { numRetries: 2, notifyUponError: false },
        );

        // store version and set saved payload as source
        setFormEntity({
            documentVersion: Number(response.documentVersion),
            changedVariables: formattedPayload,
        });
        // reset changed form keys after the form is updated
        // so that the user is not tracked after these fields have been changed and saved
        // setChangedFormKeys();

        return response;
    } catch (error) {
        if (error.response.status === ERROR_STATUS_CODE_406) {            
            notifyError("Something went wrong. Please refresh the page and try again.", error);
        } else if (error.response.status === ERROR_STATUS_CODE_409) {
            const { value: version, message } = await error.response.json();

            // do nothing and retry save form payload
            if (message.includes("Can not acquire lock")) {
                // retry update
                return updateFormEntity(payload);
            }

            // store last valid version
            setFormEntity({ documentVersion: Number(version) });

            // compare form data and get conflicts and result of comparing
            const { conflicts } = await compareFormData(
                payload,
                clearNextRequest
            );

            if (isNonEmptyArray(conflicts)) {
                throw {
                    conflictingFields: formatConflictingFields(conflicts),
                };
            }
        } else {
            throw error;
        }
    }
};

const reviewSection = async (apiRequest, args) => {
    try {
        const response = await apiRequest(...args);

        setFormEntity({ documentVersion: Number(response.documentVersion) });

        return response;
    } catch (error) {
        if (error.response.status === ERROR_STATUS_CODE_409) {
            const { value: version } = await error.response.json();
            const {isEditMode} = store.getState().form.entity;

            if(!isEditMode) {
                // compare form data and result of comparing
                const { mergedData } = await compareFormData();

                // store last version and compared data
                setFormEntity({
                    documentVersion: Number(version),
                    // actualVariables: mergedData,
                    changedVariables: mergedData,
                    // variables: mergedData,
                });
            }

        	// override with actual version
        	const [ params, queryParams ] = args;
        	queryParams.version = version;

        	return reviewSection(apiRequest, [ params, queryParams ]);

        } else {
        	const message = IS_DEV && isNonEmptyString(error.message)
        			? error.message
        			: store.getState().dictionary.common.formResponse.unableLoadData;
        	notifyError(message, error);
        }
    }
};

export const setReviewedSection = (params, documentVersion, modificationNumber) => {
    return reviewSection(api.form.setReviewedSection, [params, { version: documentVersion, modificationNumber: modificationNumber }]).then(
        (response) => {
            setFormEntity({
                documentVersion: Number(response.documentVersion),
                modificationNumber: Number(response.modificationNumber)
            });

            return response;
        }
    );
};

export const removeReviewedSection = (params, documentVersion, modificationNumber) => {
    return reviewSection(api.form.removeReviewedSection, [params, { version: documentVersion, modificationNumber: modificationNumber }]).then(
        (response) => {
            setFormEntity({
                documentVersion: Number(response.documentVersion),
                modificationNumber: Number(response.modificationNumber)
            });

            return response;
        }
    );
};

export const setFormSavingIndicator = (payload) => {
    dispatch({
        type: constants.FORM_ENTITY_SET_INDICATOR_IS_SAVING,
        payload,
    });
};

export const setFormEditMode = (payload) => {
    dispatch({
        type: constants.FORM_EDIT_MODE,
        payload,
    });
};

export const setFormEditLocked = (payload) => {
    dispatch({
        type: constants.FORM_EDIT_LOCKED,
        payload,
    });
};

export const setFormTimeoutInfo = (payload) => {
    dispatch({
        type: constants.FORM_TIMEOUT_INFO,
        payload,
    });
};

export const setFormEntityData = (payload) => {
    dispatch({
        type: constants.FORM_ENTITY_SET_DATA,
        payload,
    });
};

export const setFormEntityChanged = (payload) => {
    dispatch({
        type: constants.FORM_ENTITY_CHANGED,
        payload,
    });
};

export const setFileUploading = (payload) => {
    dispatch({
        type: constants.FORM_ENTITY_SET_FILE_UPLOADING,
        payload,
    });
};
